import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createContext, use, type ReactNode } from "react";

import { API_ERROR_CODES, ApiError } from "@/api/client";

import type { Bootstrap, BootstrapSource } from "@/bridge/bootstrap";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A WebView screen is short-lived and the user cannot refresh it by hand,
      // so refetching on focus buys nothing and costs a request.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      // Retry a dropped connection once; never retry an answer the API already
      // gave us (401, or a business rejection) — that only delays the error the
      // screen needs to show.
      retry: (failureCount, error) =>
        failureCount < 1 &&
        error instanceof ApiError &&
        error.code === API_ERROR_CODES.network,
      // "online" mode parks a query in `fetchStatus: "paused"` while the
      // browser reports itself offline, which the UI can only render as a
      // spinner that never resolves. Inside a WebView we would rather attempt
      // the request and show a real error.
      networkMode: "always",
    },
  },
});

export type BootstrapContextValue = {
  readonly data: Bootstrap;
  readonly source: BootstrapSource;
};

const BootstrapContext = createContext<BootstrapContextValue | null>(null);

export function useBootstrap(): BootstrapContextValue {
  const value = use(BootstrapContext);
  if (value === null) {
    throw new Error("useBootstrap must be used inside <AppProviders>.");
  }
  return value;
}

export function AppProviders({
  bootstrap,
  children,
}: {
  bootstrap: BootstrapContextValue;
  children: ReactNode;
}) {
  return (
    <BootstrapContext value={bootstrap}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </BootstrapContext>
  );
}
